/**
 * InvestmentStrategyNudge — dashboard card reminding users to complete the
 * mandatory 10-question Investment Objective & Risk Tolerance questionnaire.
 *
 * Auto-hides once `user.has_completed_investment_strategy` is truthy.
 * Dismissible for 12 hours so it doesn't nag on every dashboard reload.
 * Responsive: stacks vertically on mobile, horizontal on wider screens.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, X, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const DISMISS_KEY = "md_strategy_nudge_dismissed_at";
const DISMISS_TTL_HOURS = 12;

export default function InvestmentStrategyNudge({ locked = false }) {
    const { user } = useAuth();
    const [dismissed, setDismissed] = useState(false);
    const [strategyMeta, setStrategyMeta] = useState(null);
    const [loaded, setLoaded] = useState(false);

    // Check dismiss state from localStorage
    useEffect(() => {
        if (locked) return; // locked = mandatory step, dismiss state irrelevant
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (!raw) return;
            const ts = parseInt(raw, 10);
            if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_HOURS * 3600 * 1000) {
                setDismissed(true);
            }
        } catch { /* ignore */ }
    }, [locked]);

    // Fetch strategy status for completed users (to show their profile label)
    useEffect(() => {
        if (!user) return;
        if (!user.has_completed_investment_strategy) {
            setLoaded(true);
            return;
        }
        api.get("/investment-strategy/mine")
            .then(({ data }) => setStrategyMeta(data.strategy))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, [user]);

    if (!user) return null;
    if (!loaded) return null;

    // Already completed — don't show the nudge
    if (user.has_completed_investment_strategy) return null;

    // Dismissed this window (never true while locked — mandatory step)
    if (!locked && dismissed) return null;

    function onDismiss() {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    }

    return (
        <div
            data-testid="investment-strategy-nudge"
            className="mb-6 border-2 border-amber-500/40 bg-amber-500/5 rounded-xl p-4 sm:p-5"
        >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Icon + Copy */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center shrink-0">
                        <ShieldCheck className="w-5 h-5" strokeWidth={2.25} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-600 mb-0.5">
                            Required to trade
                        </div>
                        <div className="text-sm sm:text-base font-bold text-foreground leading-snug">
                            Complete your Investment Strategy
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            Ten questions that set your risk profile, trading permissions, and
                            how Kimani &amp; Obondo personalise guidance. Takes about 2 minutes.
                        </div>
                    </div>
                </div>

                {/* CTA + Dismiss */}
                <div className="flex items-center gap-2 shrink-0 sm:flex-col sm:items-end">
                    <Link
                        to="/onboarding/investment-strategy"
                        data-testid="investment-strategy-nudge-cta"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 text-white px-4 py-2 text-xs sm:text-sm font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap"
                    >
                        <FileText className="w-3.5 h-3.5" />
                        Start questionnaire
                        <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    {!locked && (
                        <button
                            type="button"
                            aria-label="Dismiss reminder"
                            data-testid="investment-strategy-nudge-dismiss"
                            onClick={onDismiss}
                            className="text-muted-foreground/50 hover:text-foreground transition-colors p-1"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
